'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useSwiper } from 'swiper/react';
import 'swiper/swiper-bundle.css';

import Text from '@commons/components/Text';
import Flex, { type FlexProps } from '@commons/containers/Flex';

import IconButton from '../IconButton';
import styles from './index.module.scss';

// Type definition for the CarouselControls component properties.

type CarouselControlsProps = FlexProps & {
  displayCounter?: boolean;
  displayNavigation?: boolean;
  displayPagination?: boolean;
};

/**
 * Carousel controls component.
 */

export const CarouselControls = (props: CarouselControlsProps) => {
  const t = useTranslations('commons');
  const swiper = useSwiper();
  const [activeIndex, setActiveIndex] = useState(swiper?.activeIndex || 0);

  const {
    displayPagination = false,
    displayNavigation = true,
    displayCounter = false,
    className,
    ...rest
  } = props;

  /**
   * Update active index and total slides on Swiper events.
   */
  useEffect(() => {
    if (!swiper) return;

    const handleUpdate = () => {
      setActiveIndex(swiper.activeIndex);
    };

    swiper.on('slideChange', handleUpdate);

    // Cleanup event listeners on component unmount
    return () => {
      swiper.off('slideChange', handleUpdate);
    };
  }, [swiper]);

  const start = String(activeIndex + 1).padStart(2, '0');
  const end = String(swiper?.slides.length || 1).padStart(2, '0');

  const classes = clsx(styles.controls, className);

  return (
    <Flex align="center" justify="space-between" direction="row" className={classes} {...rest}>
      <Flex gap="sm" align="center">
        {displayCounter && (
          <Text marginBottom="none" level="label-lg" className={styles.counter}>
            <span>{start}</span>
            <span className={styles.end}>{` / ${end}`}</span>
          </Text>
        )}
        {displayNavigation && (
          <Flex gap={4} className={styles.navigation}>
            <IconButton
              type="chevronLeft"
              title={t('previous')}
              aria-label={t('previous')}
              className={clsx(styles.prev, styles.arrow)}
            />
            <IconButton
              type="chevronRight"
              title={t('next')}
              aria-label={t('next')}
              className={clsx(styles.next, styles.arrow)}
            />
          </Flex>
        )}
      </Flex>
      {displayPagination && <Flex.Item className={styles.pagination} />}
    </Flex>
  );
};

/**
 * Carousel Controls Loading component.
 *
 * The HTML returned by Swiper during SSR causes layout shifts, causing issues with other features like anchor links.
 * This component can be used to render an initial loading state of the controls.
 */

export const CarouselControlsLoading = () => <div className={styles.controls} />;

export default CarouselControls;
